/**
 * Turning an SDK result into something TanStack Query understands.
 *
 * openapi-fetch returns `{ data } | { error }` rather than throwing, which is the right shape for
 * a client and the wrong one for a query: a hook that resolved successfully with `error` set
 * would put a failed read into the cache as data. So the boundary is here, and it is the only
 * place that decides what "failed" means.
 *
 * Conflicts do NOT pass through this function. The SDK's middleware has already thrown a typed
 * ConflictError for 409/412/428 (T-12) before the result exists, which is exactly why the console
 * can handle "somebody else changed this" differently from "the request was wrong".
 */

/**
 * A 2xx whose body is not JSON.
 *
 * This is the failure prompt 13 was written about. The console called `/agreements`, the reverse
 * proxy had no route for it, the SPA fallback answered `index.html` with a 200, and the parse died
 * three components deep on `Unexpected token '<'` — an error that named neither the route nor the
 * cause. It will happen again the next time a route is added to the console before the proxy, so
 * it is caught at the boundary and says both.
 *
 * It is separate from [ApiError] because it is a different kind of wrong: the request succeeded,
 * and what came back is not this API's answer at all.
 */
export class NonJsonResponseError extends Error {
  override readonly name = 'NonJsonResponseError';
  readonly status: number;
  readonly contentType: string;
  readonly url: string;

  constructor(status: number, contentType: string, url: string) {
    super(
      `Request to ${url} returned ${String(status)} with content-type ` +
        `${contentType === '' ? '(none)' : contentType}, not JSON. ` +
        'The most likely cause is that this path has no backend route and something upstream — ' +
        'a reverse proxy, an SPA fallback — answered instead.',
    );
    this.status = status;
    this.contentType = contentType;
    this.url = url;
  }
}

export function isNonJsonResponseError(error: unknown): error is NonJsonResponseError {
  return error instanceof NonJsonResponseError;
}

/** `application/json; charset=utf-8` and `application/problem+json` both count. */
function isJsonContentType(contentType: string): boolean {
  const essence = contentType.split(';')[0]?.trim().toLowerCase() ?? '';
  return essence === 'application/json' || essence.endsWith('+json');
}

export class ApiError extends Error {
  override readonly name = 'ApiError';
  readonly status: number;
  readonly body: unknown;
  /** Present when the gateway's error envelope names one; the UI keys messages off it. */
  readonly code: string | undefined;

  constructor(status: number, body: unknown, url: string) {
    super(`Request to ${url} failed with ${String(status)}.`);
    this.status = status;
    this.body = body;
    this.code = readCode(body);
  }
}

function readCode(body: unknown): string | undefined {
  if (typeof body !== 'object' || body === null) return undefined;
  const code: unknown = (body as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}

export interface SdkResult<T> {
  readonly data?: T | undefined;
  readonly error?: unknown;
  readonly response: Response;
}

export async function unwrap<T>(result: Promise<SdkResult<T>>): Promise<T> {
  const { data, error, response } = await result;
  if (!response.ok) throw new ApiError(response.status, error, response.url);

  // Checked BEFORE the body is looked at. openapi-fetch hands back a non-JSON body as a string
  // rather than throwing, so without this a page of HTML would flow on as `data` and fail as a
  // shape error somewhere that has no idea what a reverse proxy is.
  //
  // A 204 has no body and no content-type, and is not this failure — it is handled below.
  const contentType = response.headers.get('content-type') ?? '';
  if (response.status !== 204 && !isJsonContentType(contentType)) {
    throw new NonJsonResponseError(response.status, contentType, response.url);
  }

  if (data === undefined) {
    // A 2xx with no body is a contract the console cannot act on; say so where it happened
    // rather than three components deep.
    throw new ApiError(response.status, error, response.url);
  }
  return data;
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isNotFound(error: unknown): boolean {
  return isApiError(error) && error.status === 404;
}
